import { IPropertyAddressData, propertyAddressToString } from '../address/IPropertyAddressData';
import { eConsumerTypes } from '../order/consumer/eConsumerTypes';
import { eSigningAppointmentStatus } from './eSigningAppointmentStatus';
import { INotaryDetailsData } from './INotaryDetailsData';
import { ISignerResponse } from './ISignerResponse';
import { convertTimeFromISO, getDateString, getMillisFromDateAndTime } from '../../ui-toolkit/utilities/getDateString';
import { IPropertyAddressResponse } from '../address/IPropertyAddressResponse';
import { ISigningAppointmentDocumentResponse } from './ISigningAppointmentDocumentResponse';
import { buildTimeStamp, getCurrentDate, getCurrentTime, isDateGreaterThan } from '../../utilities/timeHelpers';
import { getLanguageName } from '../../utilities/getListOfLanguages';
import { ISigningAppointmentRescheduleResponse } from './ISigningAppointmentRescheduleResponse';
import { ICorrectionReason } from 'types/corrections/ICorrection';
import { UUID } from '../shared/UUID';
import { ISigningAppointmentScanbackManifest } from './ISigningAppointmentScanbackManifest';
import { eSigSyncProgressionLine, ISigSyncProgressionLine } from './ISigSyncProgressionLine';
import { getSigningAppointmentOwnerTypeDisplayLabel } from '../../utilities/order/scheduling/getSigningAppointmentOwnerTypeDisplayLabel';
import { getFormDataOwnerType } from '../order/consumer/eCreateSigningAppointmentFormDataOwnerTypes';
import { DssAppointmentOption } from './CreateSigningAppointmentRequest';
import { getDssDateTimeInfo } from 'components/order/scheduling/utilities/getDssDateTimeInfo';
import { IDigitalSchedulingAccessResponse } from './IDigitalSchedulingAccessResponse';
import { eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import { ePartyPurpose } from '../../ui-toolkit/enums/ePartyPurpose';

export interface ISigningAppointmentResponse {
  id: string;
  address?: IPropertyAddressData | null;
  ownerType: eConsumerTypes;
  status?: eSigningAppointmentStatus | string;
  createdAt?: string;
  updatedAt?: string;
  appointmentDate?: string | null;
  appointmentTime?: string | null;
  availableStartDate?: string;
  availableEndDate?: string;
  signingType?: string;
  signingMethod?: string;
  rateCents?: number;
  language?: string;
  isDateRange?: boolean;
  timeZone?: string;
  abbreviatedTimeZone?: string;
  notary?: INotaryDetailsData;
  notes?: string;
  signers?: ISignerResponse[];
  isConfirmed?: boolean;
  witness?: number;
  isSignatureSyncAppointment?: boolean;
  isDigitalScheduling?: boolean;
  isDigitalSchedulingDraft?: boolean;
  dssAppointmentOptions?: DssAppointmentOption[] | null;
  isDigitalSchedulingConfirmed?: boolean;
  documents: ISigningAppointmentDocumentResponse[];
  reschedules: ISigningAppointmentRescheduleResponse[];
  correctionTriggeredById?: UUID;
  correctionReasons?: ICorrectionReason[];
  scanbackManifest?: ISigningAppointmentScanbackManifest;
  dssAccess?: IDigitalSchedulingAccessResponse;
  signerWillProvideLocation?: boolean;
  signerSelectsFromRange?: boolean;
  isDssDateRange?: boolean;
  dssDate?: string;
  dssStartTime?: string;
  dssEndTime?: string;
  dssDateRangeStart?: string;
  dssDateRangeEnd?: string;
}

export const formatDisplayDate = (appointmentDate: string): string => {
  return (appointmentDate && getDateString(`LLLL d, yyyy`, appointmentDate)) || '';
};

export const formatDisplayTime = (appointmentTime: string): string => {
  return appointmentTime ? convertTimeFromISO(appointmentTime, 'HH:mm:ss') : '';
};

export const formatSigningAppointmentDateTime = (
  date?: string,
  time?: string,
  timezone?: string,
  startDate?: string,
  endDate?: string
): string => {
  if (date) {
    let formattedDateTime: string = formatDisplayDate(date);
    formattedDateTime = time ? formattedDateTime + ' at ' + formatDisplayTime(time) : formattedDateTime;
    formattedDateTime = time && timezone ? formattedDateTime + ' ' + timezone : formattedDateTime;
    return formattedDateTime;
  }
  if (startDate && endDate) {
    return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
  }
  return '';
};

export const getSigningAppointmentDisplayDateTime = (appointment: ISigningAppointmentResponse): string => {
  const {
    appointmentDate,
    appointmentTime,
    abbreviatedTimeZone,
    availableStartDate,
    availableEndDate,
    dssAppointmentOptions,
  } = appointment;
  if (appointmentDate && appointmentTime) {
    return formatSigningAppointmentDateTime(
      appointmentDate,
      appointmentTime,
      abbreviatedTimeZone,
      availableStartDate,
      availableEndDate
    );
  }
  if (dssAppointmentOptions && !appointmentDate && !appointmentTime) {
    return getDssDateTimeInfo(dssAppointmentOptions);
  }
  return formatSigningAppointmentDateTime(
    appointmentDate as string,
    appointmentTime as string,
    abbreviatedTimeZone,
    availableStartDate,
    availableEndDate
  );
};

export const getSigningAppointmentDisplayAddress = (appointment: ISigningAppointmentResponse): string => {
  if (appointment.address && !appointment.isDigitalScheduling && !appointment.isDigitalSchedulingDraft) {
    return propertyAddressToString(appointment.address as IPropertyAddressResponse);
  }

  if (appointment.isDigitalScheduling || appointment.isDigitalSchedulingDraft) {
    if (!appointment.address) {
      return 'Location Pending Selection';
    }
    return propertyAddressToString(appointment.address as IPropertyAddressResponse);
  }

  return 'Location Pending Selection';
};

export const getSigningAppointmentTransactionCategoryDisplayLabel = (
  appointment: ISigningAppointmentResponse,
  orderTransactionCategory: eTransactionCategory,
  shortTitle?: boolean
): string => {
  return getSigningAppointmentOwnerTypeDisplayLabel(
    getFormDataOwnerType(appointment.ownerType, appointment.isDigitalScheduling),
    orderTransactionCategory,
    !!appointment.correctionTriggeredById,
    shortTitle
  );
};

export const getSigningAppointmentPartyPurposeDisplayLabel = (
  appointment: ISigningAppointmentResponse,
  partyPurpose: ePartyPurpose
): string => {
  if (appointment.ownerType === eConsumerTypes.seller) {
    return 'Seller Appointment';
  }
  if (appointment.ownerType === eConsumerTypes.borrower) {
    if (partyPurpose === ePartyPurpose.buyer) {
      return 'Buyer Appointment';
    }
    if (partyPurpose === ePartyPurpose.borrower) {
      return 'Borrower Appointment';
    }
  }

  throw Error(`Invalid owner type ${appointment.ownerType} and party purpose ${ePartyPurpose} for signing appointment`);
};

// the higher, the most important for ordering
const PrioritiesByStatus: Record<eSigningAppointmentStatus, number> = {
  requested: 10,
  scheduled: 10,
  complete: 2,
  cancelled: 1,
  'reschedule-required': 10,
  'documents-shared': 10,
};

export const signingAppointmentComparator = (
  a: ISigningAppointmentResponse,
  b: ISigningAppointmentResponse
): number => {
  const priorityA = a.status ? PrioritiesByStatus[a.status as eSigningAppointmentStatus] : -1;
  const priorityB = b.status ? PrioritiesByStatus[b.status as eSigningAppointmentStatus] : -1;

  if (priorityA === priorityB) {
    const dateA = a.appointmentDate || a.availableStartDate;
    const dateB = b.appointmentDate || b.availableStartDate;

    if (dateA && dateB) {
      const millisA = getMillisFromDateAndTime(dateA, a.appointmentTime as string, a.timeZone);
      const millisB = getMillisFromDateAndTime(dateB, b.appointmentTime as string, b.timeZone);
      return millisA - millisB;
    } else {
      return 0;
    }
  } else {
    return priorityB - priorityA;
  }
};

export const sortSigningAppointments = (appointments: ISigningAppointmentResponse[]): ISigningAppointmentResponse[] => {
  return appointments.toSorted(signingAppointmentComparator);
};

// Returns corrections needed for an appointment to be converted or integrated with SigSync
export const getCorrectionsForSigSync = ({
  abbreviatedTimeZone,
  address,
  appointmentTime,
  appointmentDate,
  signers,
}: ISigningAppointmentResponse): string[] => {
  let requiredActions: string[] = [];

  if (!address) {
    requiredActions.push('Set a valid address.');
  }

  if (!appointmentDate) {
    requiredActions.push('Set a valid appointment date.');
  } else if (isDateGreaterThan(getCurrentDate(), appointmentDate)) {
    requiredActions.push('Set an appointment date greater than today.');
  }

  if (!signers || signers.length === 0) {
    requiredActions.push('Include at least one signer to the appointment.');
  }

  if (!appointmentTime) {
    requiredActions.push('Set a valid appointment time.');
  } else if (
    appointmentDate &&
    isDateGreaterThan(getCurrentTime(), buildTimeStamp(appointmentDate, appointmentTime, abbreviatedTimeZone))
  ) {
    requiredActions.push('Set an appointment date and time greater than now.');
  }

  return requiredActions;
};

export const getSigningAppointmentDisplayLanguage = ({ language }: ISigningAppointmentResponse): string => {
  if (!language) {
    return 'N/A';
  }
  return getLanguageName(language) || language;
};

// A set of Signing Appointment Statuses in a terminal state.
// Chloe cannot edit an appointment in a terminal state.
const setOfTerminatedStatuses = new Set<string | undefined>([
  eSigningAppointmentStatus.Cancelled,
  eSigningAppointmentStatus.Complete,
]);

// A set of SigSync Progression Statuses in a terminal state.
// Chloe cannot edit an appointment in a terminal state.
const setOfTerminatedSigSyncProgressionStatuses = new Set<string | null | undefined>([
  eSigSyncProgressionLine.Canceled,
  eSigSyncProgressionLine.Complete,
  eSigSyncProgressionLine.NotSigned,
]);

export const signingAppointmentIsEditable = (
  { status, isSignatureSyncAppointment }: ISigningAppointmentResponse,
  progressionStatus: ISigSyncProgressionLine | undefined | null
) => {
  if (setOfTerminatedStatuses.has(status)) {
    return false;
  }
  if (isSignatureSyncAppointment) {
    return !setOfTerminatedSigSyncProgressionStatuses.has(progressionStatus);
  }
  return true;
};
